import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { ProductionBadge } from "@/components/ds/ProductionBadge";
import { StaleSyncBanner } from "@/components/ds/StaleSyncBanner";
import { Confidence } from "@/components/ds/Severity";

const STATUS_META = {
    ok: { Icon: CheckCircle2, label: "Ready", cls: "bg-emerald-50 text-emerald-800 border-emerald-100" },
    warning: { Icon: AlertTriangle, label: "Attention", cls: "bg-amber-50 text-amber-800 border-amber-100" },
    blocker: { Icon: XCircle, label: "Blocked", cls: "bg-red-50 text-red-800 border-red-100" },
    info: { Icon: Info, label: "Info", cls: "bg-stone-50 text-stone-600 border-stone-200" },
};

const READINESS_META = {
    ready: {
        label: "Ready for production",
        headlineCls: "bg-emerald-50 border-emerald-200 text-emerald-900",
    },
    ready_with_warnings: {
        label: "Ready — attention required",
        headlineCls: "bg-amber-50 border-amber-200 text-amber-900",
    },
    blocked: {
        label: "Blocked — not safe to switch on",
        headlineCls: "bg-red-50 border-red-200 text-red-900",
    },
};

function StatusPill({ status }) {
    const meta = STATUS_META[status] || STATUS_META.info;
    const { Icon } = meta;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-wider font-medium rounded-full ${meta.cls}`}
            data-testid={`status-pill-${status}`}
        >
            <Icon size={11} strokeWidth={2} />
            {meta.label}
        </span>
    );
}

function EntityRow({ entity }) {
    return (
        <li
            className="grid grid-cols-[1fr_auto] gap-4 items-start pb-4 border-b border-stone-100 last:border-0 last:pb-0"
            data-testid={`entity-row-${entity.id}`}
        >
            <div>
                <div className="flex items-center gap-3 mb-1">
                    <span className="text-[14px] font-medium text-stone-900">
                        {entity.display_name}
                    </span>
                    <ProductionBadge state={entity.data_mode} />
                </div>
                <div className="text-[12.5px] text-stone-500 mt-0.5">
                    Last sync: {entity.freshness_human}
                </div>
                <div className="text-[13px] text-stone-700 mt-2">
                    <span className="text-stone-500">Next step: </span>
                    {entity.next_action}
                </div>
            </div>
            <div className="text-right">
                <Confidence level={entity.confidence} />
            </div>
        </li>
    );
}

// Group-level stale-sync banner: only when the group itself is materially stale
// (any production_live entity > 24h, OR any production_error, OR any mock when
// at least one entity is already live). Calm, single banner.
function deriveGroupStaleness(entities) {
    const live = entities.filter((e) => e.data_mode === "production_live");
    if (live.length === 0) return null;
    const stale = live
        .filter((e) => (e.freshness_hours ?? 0) >= 24)
        .sort((a, b) => (b.freshness_hours ?? 0) - (a.freshness_hours ?? 0));
    if (stale.length === 0) return null;
    const worst = stale[0];
    return {
        scope: "group",
        contextLabel: `${stale.length} live ${stale.length === 1 ? "entity" : "entities"}`,
        freshnessHours: worst.freshness_hours,
        nextAction: "Re-sync the affected entities before relying on these numbers.",
    };
}

export default function ProductionReadiness() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/production-readiness");
            setData(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading || !data)
        return (
            <PageShell>
                <Loading label="Inspecting credentials, sync history and entity state…" testId="production-readiness-loading" />
            </PageShell>
        );

    const meta = READINESS_META[data.readiness] || READINESS_META.blocked;
    const blockers = data.checks.filter((c) => c.status === "blocker");
    const warnings = data.checks.filter((c) => c.status === "warning");
    const oks = data.checks.filter((c) => c.status === "ok" || c.status === "info");
    const entities = data.entities || [];
    const staleBanner = deriveGroupStaleness(entities);

    return (
        <PageShell width="default" testId="production-readiness-page">
            <PageHeader
                eyebrow="Operations"
                title="Are we safe to switch on?"
                subtitle="Deterministic inspection of credentials, encryption keys, scheduler wiring and per-entity data mode. The checklist to run before switching from demo data to live MYOB / bank feeds."
                action={
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md text-stone-700 bg-stone-100 hover:bg-stone-200"
                        data-testid="refresh-readiness"
                    >
                        <RefreshCw size={13} strokeWidth={2} />
                        Re-run checks
                    </button>
                }
            />

            {staleBanner ? (
                <StaleSyncBanner
                    scope={staleBanner.scope}
                    contextLabel={staleBanner.contextLabel}
                    freshnessHours={staleBanner.freshnessHours}
                    nextAction={staleBanner.nextAction}
                    testId="group-stale-banner"
                />
            ) : null}

            <div
                className={`border-l-4 px-5 py-4 mb-6 rounded-r-md ${meta.headlineCls}`}
                data-testid="readiness-headline"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-medium opacity-80">
                            Overall
                        </div>
                        <div className="text-display-md mt-1">{meta.label}</div>
                        <div className="text-[12px] mt-2 opacity-80">
                            {data.blocker_count} blocker · {data.warning_count} attention · {data.checks.length} total checks · as of {fmtDateTime(data.as_of)}
                        </div>
                    </div>
                    {data.group_confidence ? (
                        <div
                            className="text-right"
                            data-testid="group-confidence"
                        >
                            <div className="text-[11px] uppercase tracking-wider font-medium opacity-80">
                                Group confidence
                            </div>
                            <div className="mt-1.5">
                                <Confidence level={data.group_confidence} />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <Section
                title="Entity data mode"
                subtitle="How many entities are on demo data versus live MYOB."
            >
                <Card variant="default" padding="lg" testId="data-mode-breakdown">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-5">
                        <Stat
                            label="Live MYOB data"
                            value={data.data_mode_breakdown.production_live ?? 0}
                            money={false}
                            size="sm"
                            tone={(data.data_mode_breakdown.production_live ?? 0) > 0 ? "positive" : "default"}
                            testId="data-mode-production_live"
                        />
                        <Stat
                            label="Connecting"
                            value={data.data_mode_breakdown.production_pending ?? 0}
                            money={false}
                            size="sm"
                            testId="data-mode-production_pending"
                        />
                        <Stat
                            label="Needs attention"
                            value={data.data_mode_breakdown.production_error ?? 0}
                            money={false}
                            size="sm"
                            tone={(data.data_mode_breakdown.production_error ?? 0) > 0 ? "negative" : "default"}
                            testId="data-mode-production_error"
                        />
                        <Stat
                            label="Demo data"
                            value={data.data_mode_breakdown.mock ?? 0}
                            money={false}
                            size="sm"
                            testId="data-mode-mock"
                        />
                        <Stat
                            label="Total entities"
                            value={data.data_mode_breakdown.total_entities ?? 0}
                            money={false}
                            size="sm"
                            testId="data-mode-total_entities"
                        />
                    </div>
                </Card>
            </Section>

            {entities.length > 0 ? (
                <Section
                    title="Per-entity trust state"
                    subtitle="State transition, last sync, freshness-degraded confidence, and the next safe action."
                >
                    <Card variant="default" padding="lg" testId="entity-trust-strip">
                        <ul className="space-y-4">
                            {entities.map((e) => (
                                <EntityRow key={e.id} entity={e} />
                            ))}
                        </ul>
                    </Card>
                </Section>
            ) : null}

            {[
                { title: "Must fix before production", items: blockers, testId: "blockers-section" },
                { title: "Attention required", items: warnings, testId: "warnings-section" },
                { title: "Healthy", items: oks, testId: "oks-section" },
            ].map((group) =>
                group.items.length === 0 ? null : (
                    <Section
                        key={group.title}
                        title={group.title}
                        subtitle={`${group.items.length} check${group.items.length === 1 ? "" : "s"}`}
                    >
                        <Card variant="default" padding="lg" testId={group.testId}>
                            <ul className="space-y-4">
                                {group.items.map((c) => (
                                    <li
                                        key={c.key}
                                        className="grid grid-cols-[120px_1fr] gap-4 items-start pb-4 border-b border-stone-100 last:border-0 last:pb-0"
                                        data-testid={`check-${c.key}`}
                                    >
                                        <div>
                                            <StatusPill status={c.status} />
                                        </div>
                                        <div>
                                            <div className="text-[14px] font-medium text-stone-900">
                                                {c.label}
                                            </div>
                                            <div className="text-[13px] text-stone-600 mt-1 leading-relaxed">
                                                {c.detail}
                                            </div>
                                            {c.action && (
                                                <div className="text-[12.5px] text-stone-500 mt-2">
                                                    <span className="text-stone-700 font-medium">Action:</span>{" "}
                                                    {c.action}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    </Section>
                ),
            )}
        </PageShell>
    );
}
